/**
 * StatusTimeline — the request-detail vertical timeline (ux-flows §1.E). Each wire transition is one
 * warm milestone (timeline.ts), oldest → newest top-to-bottom, with a connecting thread down the
 * dots. The latest milestone is the live "you are here"; terminal outcomes (declined / expired) mute
 * it, `signed` turns it gold. Never colour alone — every dot sits beside its text label.
 */
import { cn } from '@/lib/utils';
import { formatRelative } from '@/lib/ledger';
import { toMilestones, type Milestone } from '@/lib/timeline';
import type { RequestTimelineEntry } from '@/lib/api-client';

interface StatusTimelineProps {
  timeline: RequestTimelineEntry[];
  refereeName: string;
}

/** The dot treatment for a milestone (current gets emphasis; terminal/positive outcomes recolour). */
function dotClass(m: Milestone): string {
  if (!m.current) return 'bg-sage';
  switch (m.status) {
    case 'signed':
      return 'bg-gold shadow-[0_0_0_3px_var(--gold-tint)]';
    case 'declined':
    case 'expired':
    case 'failed':
      return 'bg-ink-38';
    default:
      return 'dot-live bg-coral shadow-[0_0_0_3px_var(--coral-tint)]';
  }
}

export function StatusTimeline({ timeline, refereeName }: StatusTimelineProps) {
  const milestones = toMilestones(timeline, refereeName);
  if (milestones.length === 0) return null;

  return (
    <ol className="relative mt-1" aria-label="Status timeline">
      {milestones.map((m, i) => {
        const last = i === milestones.length - 1;
        return (
          <li key={`${m.status}-${i}`} className="relative flex gap-3 pb-4 last:pb-0">
            {/* the connecting thread, drawn behind the dots (not under the final one) */}
            {!last && <span className="absolute left-[5px] top-3.5 h-full w-px bg-line" aria-hidden="true" />}
            <span
              className={cn('relative z-[1] mt-1 h-[11px] w-[11px] flex-none rounded-full', dotClass(m))}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  'font-body text-[14px] leading-snug',
                  m.current ? 'font-semibold text-ink' : 'text-ink',
                )}
              >
                {m.label}
              </p>
              <p className="mt-0.5 font-body text-[12px] text-ink-quiet">{formatRelative(m.occurredAt)}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
